
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cozeApiService } from '../../services/cozeApi';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  data?: any; // 用于存储结构化数据
}

export default function ToolDetail() {
  const [searchParams] = useSearchParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [displayData, setDisplayData] = useState<any>(null);
  const [displayType, setDisplayType] = useState<'report' | 'summary' | 'json'>('report');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // 从URL参数获取工具信息
  const toolName = searchParams.get('toolName') || '智能工作汇报助手';
  const functionName = searchParams.get('functionName') || '';
  const userRole = searchParams.get('role') || '';
  const userName = searchParams.get('user') || '';

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    // 初始化 Coze API 密钥
    const defaultApiKey = 'sat_DVWes8e4MfCL27Ye2s3zJHwgBo895ofUztmBnMXyr5lcbw0Hwz2lOTY187u8uNVf';
    cozeApiService.setApiKey(defaultApiKey);

    // 添加欢迎消息
    const welcomeMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: '您好！我是智能工作汇报助手，我可以帮您：\n• 整理工作内容和成果\n• 生成工作汇报文档\n• 分析工作进展情况\n• 制作汇报PPT大纲\n\n请告诉我您需要汇报什么工作内容？',
      timestamp: Date.now()
    };
    setMessages([welcomeMessage]);
  }, [navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9);

  // 增强的智能体回复解析函数
  const parseAssistantResponse = (response: string) => {
    try {
      console.log('🔍 开始解析智能体回复:', response);

      // 清理和预处理回复内容
      let cleanedResponse = response.trim();
      
      // 移除可能的系统标识符
      cleanedResponse = cleanedResponse.replace(/ts-keyword_memory-setKeywordMemory/gi, '');
      cleanedResponse = cleanedResponse.replace(/plugin_id|msg_type|conversation_id|bot_id|user_id/gi, '');
      cleanedResponse = cleanedResponse.replace(/[{}[\]":,]/g, ' ');
      cleanedResponse = cleanedResponse.replace(/\s+/g, ' ').trim();

      // 尝试解析JSON格式的回复
      if (response.includes('{') && response.includes('}')) {
        try {
          const jsonMatch = response.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const jsonData = JSON.parse(jsonMatch[0]);
            return {
              type: 'json',
              title: '智能体结构化回复',
              data: jsonData,
              originalText: cleanedResponse,
              success: true
            };
          }
        } catch (jsonError) {
          console.log('⚠️ JSON解析失败，继续其他解析方式');
        }
      }

      // 智能内容分类和解析
      const lowerResponse = cleanedResponse.toLowerCase();
      
      // 工作汇报相关
      if (lowerResponse.includes('周报') || lowerResponse.includes('本周') || lowerResponse.includes('week')) {
        return {
          type: 'report',
          title: '工作周报',
          data: {
            period: '本周工作汇报',
            summary: cleanedResponse,
            reportType: '周报',
            generatedTime: new Date().toLocaleString('zh-CN'),
            originalResponse: response
          },
          originalText: cleanedResponse,
          success: true
        };
      } 
      
      if (lowerResponse.includes('月报') || lowerResponse.includes('本月') || lowerResponse.includes('month')) {
        return {
          type: 'report',
          title: '工作月报',
          data: {
            period: '本月工作汇报',
            summary: cleanedResponse,
            reportType: '月报',
            generatedTime: new Date().toLocaleString('zh-CN'),
            originalResponse: response
          },
          originalText: cleanedResponse,
          success: true
        };
      }
      
      if (lowerResponse.includes('项目') || lowerResponse.includes('project')) {
        return {
          type: 'summary',
          title: '项目分析报告',
          data: {
            projectName: '智能体分析项目',
            analysis: cleanedResponse,
            analysisType: '项目分析',
            generatedTime: new Date().toLocaleString('zh-CN'),
            originalResponse: response
          },
          originalText: cleanedResponse,
          success: true
        };
      }

      // PPT相关
      if (lowerResponse.includes('ppt') || lowerResponse.includes('演示') || lowerResponse.includes('幻灯片')) {
        return {
          type: 'summary',
          title: 'PPT大纲方案',
          data: {
            projectName: 'PPT制作方案',
            analysis: cleanedResponse,
            analysisType: 'PPT大纲',
            generatedTime: new Date().toLocaleString('zh-CN'),
            originalResponse: response
          },
          originalText: cleanedResponse,
          success: true
        };
      }

      // 默认工作汇报处理
      return {
        type: 'report',
        title: '智能工作汇报',
        data: {
          period: '智能体生成内容',
          summary: cleanedResponse,
          reportType: '综合汇报',
          generatedTime: new Date().toLocaleString('zh-CN'),
          originalResponse: response
        },
        originalText: cleanedResponse,
        success: true
      };

    } catch (error) {
      console.error('❌ 解析智能体回复时出错:', error);
      
      // 错误处理，返回原始内容
      return {
        type: 'report',
        title: '智能体回复',
        data: {
          period: '原始回复内容',
          summary: response || '智能体回复解析失败',
          reportType: '原始内容',
          generatedTime: new Date().toLocaleString('zh-CN'),
          originalResponse: response,
          error: error instanceof Error ? error.message : '未知错误'
        },
        originalText: response,
        success: false
      };
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage.trim();
    setInputMessage('');
    setIsLoading(true);

    try {
      console.log('🚀 发送消息给智能体:', currentInput);
      
      // 调用 Coze API
      const response = await cozeApiService.sendMessage(currentInput);
      
      console.log('📨 智能体原始回复:', response);

      if (response.success && response.message) {
        // 解析智能体的回复，提取结构化数据
        const parsedData = parseAssistantResponse(response.message);
        
        console.log('🎯 解析后的数据:', parsedData);
        
        setDisplayData(parsedData);
        setDisplayType(parsedData.type as any);

        const assistantMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: parsedData.originalText || response.message,
          timestamp: Date.now(),
          data: parsedData
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        // 如果解析成功，显示成功提示
        if (parsedData.success) {
          console.log('✅ 智能体回复解析成功，类型:', parsedData.type);
        }
      } else {
        console.error('❌ 智能体回复失败:', response.error);
        
        const errorMessage: ChatMessage = {
          id: generateId(),
          role: 'assistant',
          content: `抱歉，处理您的请求时出现错误：${response.error || '未知错误'}`,
          timestamp: Date.now()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('💥 发送消息异常:', error);
      
      const errorMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: '抱歉，系统出现错误，请稍后重试。如果问题持续存在，请检查网络连接或联系技术支持。',
        timestamp: Date.now()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 渲染智能体回复内容
  const renderReportContent = () => {
    if (!displayData) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-text-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-lg font-medium mb-2">智能体回复面板</p>
            <p className="text-sm">与右侧AI助手对话，查看智能体的回复内容</p>
          </div>
        </div>
      );
    }

    if (displayData.type === 'json') {
      return (
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
                  <p className="text-gray-600">智能体结构化数据回复</p>
                </div>
                {!displayData.success && (
                  <div className="flex items-center text-amber-600 bg-amber-50 px-3 py-1 rounded-full text-sm">
                    <i className="ri-alert-line mr-1"></i>
                    解析异常
                  </div>
                )}
              </div>
            </div>
            
            {/* 显示原始文本 */}
            {displayData.originalText && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">智能体回复内容</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{displayData.originalText}</p>
                </div>
              </div>
            )}

            {/* 显示结构化数据 */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">结构化数据</h3>
              <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
                <pre className="text-green-400 text-sm font-mono">
                  {JSON.stringify(displayData.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (displayData.type === 'summary') {
      return (
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
                  <p className="text-gray-600">基于智能体分析生成</p>
                </div>
                <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
                  <i className="ri-check-line mr-1"></i>
                  解析成功
                </div>
              </div>
            </div>

            {/* 智能体原始回复 */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-robot-line text-blue-600 mr-2"></i>
                智能体分析内容
              </h3>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {displayData.data.analysis || displayData.originalText}
                </p>
              </div>
            </div>

            {/* 分析信息 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <i className="ri-folder-line text-gray-600 mr-2"></i>
                  分析类型
                </h4>
                <p className="text-gray-700">{displayData.data.analysisType || displayData.data.projectName}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                  <i className="ri-time-line text-gray-600 mr-2"></i>
                  生成时间
                </h4>
                <p className="text-gray-700">{displayData.data.generatedTime}</p>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // 默认报告视图
      return (
        <div className="p-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="border-b border-gray-200 pb-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">{displayData.title}</h1>
                  <p className="text-gray-600">{displayData.data.period}</p>
                </div>
                <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm">
                  <i className="ri-check-line mr-1"></i>
                  {displayData.success ? '解析成功' : '部分解析'}
                </div>
              </div>
            </div>

            {/* 智能体主要回复内容 */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-robot-line text-green-600 mr-2"></i>
                  智能体回复内容
                </h3>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {displayData.data.summary}
                  </div>
                </div>
              </div>

              {/* 报告信息卡片 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <i className="ri-file-text-line text-gray-600 mr-2"></i>
                    报告类型
                  </h4>
                  <p className="text-gray-700">{displayData.data.reportType}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <i className="ri-time-line text-gray-600 mr-2"></i>
                    生成时间
                  </h4>
                  <p className="text-gray-700">{displayData.data.generatedTime}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <i className="ri-robot-line text-gray-600 mr-2"></i>
                    数据来源
                  </h4>
                  <p className="text-gray-700">Coze智能体</p>
                </div>
              </div>

              {/* 错误信息显示 */}
              {displayData.data.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2 flex items-center">
                    <i className="ri-error-warning-line text-red-600 mr-2"></i>
                    解析异常信息
                  </h4>
                  <p className="text-red-700 text-sm">{displayData.data.error}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* 左侧回复展示区域 - 75% */}
      <div className="w-3/4 bg-white border-r border-gray-200 flex flex-col">
        {/* 头部信息栏 */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                <i className="ri-robot-line text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{toolName}</h1>
                {functionName && (
                  <p className="text-sm text-gray-600">{functionName}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{userName}</span> - {userRole}
              </div>
              {displayData && (
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setDisplayType('report')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'report' ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="报告视图"
                  >
                    <i className="ri-file-text-line"></i>
                  </button>
                  <button
                    onClick={() => setDisplayType('summary')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'summary' ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="摘要视图"
                  >
                    <i className="ri-list-check-line"></i>
                  </button>
                  <button
                    onClick={() => setDisplayType('json')}
                    className={`p-2 rounded-lg transition-colors cursor-pointer ${
                      displayType === 'json' ? 'bg-green-100 text-green-600' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    title="数据视图"
                  >
                    <i className="ri-code-line"></i>
                  </button>
                </div>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors cursor-pointer"
                title="返回工作台"
              >
                <i className="ri-home-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>

        {/* 回复内容区域 */}
        <div className="flex-1 overflow-auto bg-gray-50">
          {renderReportContent()}
        </div>
      </div>

      {/* 右侧 AI 聊天区域 - 25% */}
      <div className="w-1/4 bg-white flex flex-col">
        {/* 聊天头部 */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
              <i className="ri-robot-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Coze智能体</h3>
              <p className="text-xs text-gray-500">ID: 7554655121180262463</p>
            </div>
          </div>
        </div>

        {/* 消息区域 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className={`text-xs mt-1 ${
                  message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-600"></div>
                  <span className="text-sm text-gray-600">智能体思考中...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 输入区域 */}
        <div className="p-4 border-t border-gray-200">
          <div className="space-y-2">
            {/* 快捷按钮 */}
            <div className="flex space-x-1">
              <button
                onClick={() => setInputMessage('帮我生成本周的工作汇报')}
                className="flex-1 px-2 py-1 text-xs bg-green-50 text-green-600 rounded hover:bg-green-100 transition-colors cursor-pointer"
              >
                周报
              </button>
              <button
                onClick={() => setInputMessage('帮我生成本月的工作总结')}
                className="flex-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors cursor-pointer"
              >
                月报
              </button>
              <button
                onClick={() => setInputMessage('分析当前项目的进展情况')}
                className="flex-1 px-2 py-1 text-xs bg-purple-50 text-purple-600 rounded hover:bg-purple-100 transition-colors cursor-pointer"
              >
                项目分析
              </button>
            </div>

            {/* 消息输入 */}
            <div className="flex space-x-2">
              <textarea
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="向智能体描述您的需求..."
                disabled={isLoading}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm resize-none"
                rows={2}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap self-end"
              >
                {isLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  <i className="ri-send-plane-line"></i>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
